import React, { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import { FiEye, FiTrash2 } from "react-icons/fi";
import moment from "moment";
import Swal from "sweetalert2";
import CustomTable from "../../../components/table/table";
import Loader from "../../../components/loader/loader";
import Snackbar from "../../../components/snackbar/snackbar";
import {
  admin_previous_internships_list,
  admin_previous_internships_create,
  admin_previous_internships_delete,
  institutions_list
} from "../admin.api";
import "./style.css";

export default function PreviousApplications(props) {
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);
  const [snackMessage, setSnackMessage] = useState("");
  const [isSnackError, setIsSnackError] = useState(false);
  const [snackOpen, setSnackOpen] = useState(false);

  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);

  // Modal for adding new previous applications
  const [showAddModal, setShowAddModal] = useState(false);
  const [institutions, setInstitutions] = useState([]);
  const [formFields, setFormFields] = useState({
    full_name: "",
    date_of_birth: "",
    gender: "male",
    address: "",
    email: "",
    phone_no: "",
    is_kerala_native: "1",
    course_completion_status: "completed",
    institution_id: "",
    institution_name: ""
  });

  const [photoFile, setPhotoFile] = useState(null);
  const [resumeFile, setResumeFile] = useState(null);
  const [recLetterFile, setRecLetterFile] = useState(null);

  useEffect(() => {
    fetchRecords();
  }, [page, perPage]);

  useEffect(() => {
    institutions_list()
      .then((res) => {
        if (res?.data) {
          setInstitutions(res.data);
        }
      })
      .catch((err) => console.error("Error loading institutions:", err));
  }, []);

  const handlePerPageChange = (e) => {
    setPerPage(parseInt(e.target.value));
    setPage(1);
  };

  const fetchRecords = () => {
    setIsLoading(true);
    const adjustedPerPage = perPage === "all" ? "all" : perPage;

    admin_previous_internships_list(page, adjustedPerPage)
      .then((res) => {
        if (res?.success) {
          const paginatedData = res.data?.data || [];
          paginatedData.forEach((item, index) => {
            item["slno"] = (page - 1) * (perPage === "all" ? 1 : adjustedPerPage) + index + 1;
          });
          setData(paginatedData);
          setTotalPages(perPage === "all" ? 1 : res.data?.last_page || 1);
        } else {
          setSnackMessage("Failed to load previous applications");
          setIsSnackError(true);
          setSnackOpen(true);
        }
        setIsLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching previous internships:", error);
        setSnackMessage("Error loading previous applications");
        setIsSnackError(true);
        setSnackOpen(true);
        setIsLoading(false);
      });
  };

  const snackbarClose = () => {
    setSnackMessage("");
    setIsSnackError(false);
    setSnackOpen(false);
  };

  const handleAddNew = () => {
    setFormFields({
      full_name: "",
      date_of_birth: "",
      gender: "male",
      address: "",
      email: "",
      phone_no: "",
      is_kerala_native: "1",
      course_completion_status: "completed",
      institution_id: "",
      institution_name: ""
    });
    setPhotoFile(null);
    setResumeFile(null);
    setRecLetterFile(null);
    setShowAddModal(true);
  };

  const handleDelete = (row) => {
    Swal.fire({
      title: "Delete Application?",
      text: `Are you sure you want to delete the record of ${row.full_name}?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, Delete",
      confirmButtonColor: "#dc3545",
      cancelButtonText: "Cancel",
    }).then((result) => {
      if (result.isConfirmed) {
        setIsLoading(true);
        admin_previous_internships_delete(row.id)
          .then((res) => {
            if (res?.success) {
              setSnackMessage("Record deleted successfully");
              setIsSnackError(false);
              setSnackOpen(true);
              fetchRecords();
            } else {
              setSnackMessage(res.message || "Failed to delete record");
              setIsSnackError(true);
              setSnackOpen(true);
            }
            setIsLoading(false);
          })
          .catch((err) => {
            console.error(err);
            setSnackMessage("Error deleting record");
            setIsSnackError(true);
            setSnackOpen(true);
            setIsLoading(false);
          });
      }
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormFields((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileChange = (e) => {
    const { name, files } = e.target;
    const file = files?.[0];
    if (!file) return;

    const maxBytes = 5 * 1024 * 1024;
    if (file.size > maxBytes) {
      setSnackMessage("File size must be under 5MB");
      setIsSnackError(true);
      setSnackOpen(true);
      e.target.value = "";
      return;
    }

    if (name === "photo") setPhotoFile(file);
    if (name === "resume") setResumeFile(file);
    if (name === "rec_letter_name") setRecLetterFile(file);
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!formFields.full_name || !formFields.date_of_birth || !formFields.address || !formFields.phone_no) {
      setSnackMessage("Please fill in all required fields (Name, DOB, Address, Phone No)");
      setIsSnackError(true);
      setSnackOpen(true);
      return;
    }

    setIsUpdating(true);

    const formData = new FormData();
    formData.append("full_name", formFields.full_name);
    formData.append("date_of_birth", formFields.date_of_birth);
    formData.append("gender", formFields.gender);
    formData.append("address", formFields.address);
    formData.append("email", formFields.email);
    formData.append("phone_no", formFields.phone_no);
    formData.append("is_kerala_native", formFields.is_kerala_native);
    formData.append("course_completion_status", formFields.course_completion_status);
    if (formFields.institution_id) {
      formData.append("institution_id", formFields.institution_id);
    }
    formData.append("institution_name", formFields.institution_name);

    if (photoFile) formData.append("photo", photoFile);
    if (resumeFile) formData.append("resume", resumeFile);
    if (recLetterFile) formData.append("rec_letter_name", recLetterFile);

    admin_previous_internships_create(formData)
      .then((res) => {
        if (res?.success) {
          setSnackMessage("Application created successfully");
          setIsSnackError(false);
          setSnackOpen(true);
          setShowAddModal(false);
          fetchRecords();
        } else {
          setSnackMessage(res.message || "Operation failed");
          setIsSnackError(true);
          setSnackOpen(true);
        }
        setIsUpdating(false);
      })
      .catch((err) => {
        console.error(err);
        const errMsg = err?.response?.data?.message || "An error occurred";
        setSnackMessage(errMsg);
        setIsSnackError(true);
        setSnackOpen(true);
        setIsUpdating(false);
      });
  };

  const columns = [
    { title: "ID", field: "id" },
    { title: "Full Name", field: "full_name" },
    { title: "Email", field: "email" },
    { title: "Phone", field: "phone_no" },
    { title: "Institution", field: "institution_name" },
    {
      title: "Date Applied",
      field: "created_at",
      render: (rowData) => moment(rowData.created_at).format("DD-MM-YYYY"),
    },
    {
      title: "Action",
      render: (rowData) => (
        <div className="d-flex gap-2">
          <button
            className="btn btn-sm btn-outline-primary action-btn"
            onClick={() =>
              props.history.push(`/admin-previous-applications/${rowData.id}`)
            }
            title="View Details"
          >
            <FiEye size={16} />
          </button>
          <button
            className="btn btn-sm btn-outline-danger action-btn"
            onClick={() => handleDelete(rowData)}
            title="Delete"
          >
            <FiTrash2 size={16} />
          </button>
        </div>
      ),
    },
  ];

  const renderPagination = () => {
    const pages = [];
    const maxVisible = 5;
    let start = Math.max(1, page - Math.floor(maxVisible / 2));
    let end = Math.min(totalPages, start + maxVisible - 1);

    if (end - start + 1 < maxVisible) {
      start = Math.max(1, end - maxVisible + 1);
    }

    if (start > 1) {
      pages.push(
        <button
          key={1}
          className="btn btn-sm btn-outline-primary"
          onClick={() => setPage(1)}
        >
          1
        </button>,
      );
      if (start > 2)
        pages.push(
          <span key="start-dots" className="px-1 text-primary">
            ...
          </span>,
        );
    }

    for (let i = start; i <= end; i++) {
      pages.push(
        <button
          key={i}
          className={`btn btn-sm ${page === i ? "btn-primary text-white" : "btn-outline-primary"}`}
          onClick={() => setPage(i)}
        >
          {i}
        </button>,
      );
    }

    if (end < totalPages) {
      if (end < totalPages - 1)
        pages.push(
          <span key="end-dots" className="px-1 text-primary">
            ...
          </span>,
        );
      pages.push(
        <button
          key={totalPages}
          className="btn btn-sm btn-outline-primary"
          onClick={() => setPage(totalPages)}
        >
          {totalPages}
        </button>,
      );
    }

    return pages;
  };

  return (
    <div className="container-fluid mt-4">
      <Loader open={isLoading || isUpdating} />
      <Snackbar
        message={snackMessage}
        error={isSnackError}
        open={snackOpen}
        close={snackbarClose}
      />

      <div className="card shadow-sm">
        <div className="card-header bg-white py-3 d-flex justify-content-between align-items-center">
          <h5 className="mb-0">
            <b>Previous Applications</b>
          </h5>
          <button className="btn btn-primary btn-sm px-3" onClick={handleAddNew}>
            Add New Record
          </button>
        </div>
        <div className="card-body pt-0">
          <CustomTable
            columns={columns}
            data={data}
            paging={false}
            options={{
              search: true,
              selection: false,
              exportButton: false,
            }}
          />

          <div className="d-flex justify-content-between align-items-center mt-4">
            <div className="flex-grow-1"></div>
            <div className="btn-group">
              <button
                className="btn btn-sm btn-outline-primary"
                disabled={page === 1}
                onClick={() => setPage((p) => p - 1)}
              >
                Prev
              </button>
              {renderPagination()}
              <button
                className="btn btn-sm btn-outline-primary"
                disabled={page === totalPages}
                onClick={() => setPage((p) => p + 1)}
              >
                Next
              </button>
            </div>
            <div className="flex-grow-1 d-flex justify-content-end">
              <select
                className="form-control form-control-sm w-auto ml-2"
                value={perPage}
                onChange={handlePerPageChange}
                style={{ fontSize: "12px", height: "31px" }}
              >
                <option value="5">5</option>
                <option value="10">10</option>
                <option value="25">25</option>
                <option value="50">50</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* ADD MODAL */}
      <Modal show={showAddModal} onHide={() => setShowAddModal(false)} size="lg">
        <form onSubmit={handleSubmit}>
          <Modal.Header closeButton>
            <Modal.Title>Add New Application</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="row">
              <div className="col-md-6 form-group mb-3">
                <label className="form-label">Full Name <span className="text-danger">*</span></label>
                <input
                  type="text"
                  name="full_name"
                  className="form-control"
                  value={formFields.full_name}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div className="col-md-6 form-group mb-3">
                <label className="form-label">Date of Birth <span className="text-danger">*</span></label>
                <input
                  type="date"
                  name="date_of_birth"
                  className="form-control"
                  value={formFields.date_of_birth}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div className="col-md-6 form-group mb-3">
                <label className="form-label">Gender</label>
                <select name="gender" className="form-select form-control" value={formFields.gender} onChange={handleInputChange}>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div className="col-md-6 form-group mb-3">
                <label className="form-label">Email</label>
                <input
                  type="email"
                  name="email"
                  className="form-control"
                  value={formFields.email}
                  onChange={handleInputChange}
                />
              </div>

              <div className="col-md-6 form-group mb-3">
                <label className="form-label">Phone No <span className="text-danger">*</span></label>
                <input
                  type="text"
                  name="phone_no"
                  className="form-control"
                  value={formFields.phone_no}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div className="col-md-6 form-group mb-3">
                <label className="form-label">Kerala Native</label>
                <select name="is_kerala_native" className="form-select form-control" value={formFields.is_kerala_native} onChange={handleInputChange}>
                  <option value="1">Yes</option>
                  <option value="0">No</option>
                </select>
              </div>

              <div className="col-md-6 form-group mb-3">
                <label className="form-label">Course Completion Status</label>
                <select
                  name="course_completion_status"
                  className="form-select form-control"
                  value={formFields.course_completion_status}
                  onChange={handleInputChange}
                >
                  <option value="completed">Completed</option>
                  <option value="undergoing">Undergoing</option>
                </select>
              </div>

              <div className="col-md-6 form-group mb-3">
                <label className="form-label">Academic Institution</label>
                <select
                  name="institution_id"
                  className="form-select form-control"
                  value={formFields.institution_id}
                  onChange={(e) => {
                    const instId = e.target.value;
                    const inst = institutions.find(i => String(i.id) === String(instId));
                    setFormFields((prev) => ({
                      ...prev,
                      institution_id: instId,
                      institution_name: inst ? inst.institution_name : ""
                    }));
                  }}
                >
                  <option value="">Select Institution</option>
                  {institutions.map((inst) => (
                    <option key={inst.id} value={inst.id}>
                      {inst.institution_name}
                    </option>
                  ))}
                  <option value="other">Other / Custom</option>
                </select>
              </div>

              {(!formFields.institution_id || formFields.institution_id === "other") && (
                <div className="col-md-12 form-group mb-3">
                  <label className="form-label">Custom Institution Name</label>
                  <input
                    type="text"
                    name="institution_name"
                    className="form-control"
                    placeholder="Enter Custom Institution Name"
                    value={formFields.institution_name}
                    onChange={handleInputChange}
                  />
                </div>
              )}

              <div className="col-md-12 form-group mb-3">
                <label className="form-label">Address <span className="text-danger">*</span></label>
                <textarea
                  name="address"
                  className="form-control"
                  rows="2"
                  value={formFields.address}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div className="col-md-4 form-group mb-3">
                <label className="form-label">Photo <span className="text-danger">*</span></label>
                <input
                  type="file"
                  name="photo"
                  className="form-control"
                  accept="image/*"
                  onChange={handleFileChange}
                  required
                />
              </div>

              <div className="col-md-4 form-group mb-3">
                <label className="form-label">Resume <span className="text-danger">*</span></label>
                <input
                  type="file"
                  name="resume"
                  className="form-control"
                  accept=".pdf,.doc,.docx"
                  onChange={handleFileChange}
                  required
                />
              </div>

              <div className="col-md-4 form-group mb-3">
                <label className="form-label">Rec. Letter</label>
                <input
                  type="file"
                  name="rec_letter_name"
                  className="form-control"
                  accept=".pdf,.doc,.docx"
                  onChange={handleFileChange}
                />
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <button type="button" className="btn btn-secondary" onClick={() => setShowAddModal(false)}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={isUpdating}>
              Create Record
            </button>
          </Modal.Footer>
        </form>
      </Modal>
    </div>
  );
}
